import { BUILD_PLAN_VIEW, DRINK_MIX_BUILD_PLAN_VIEW, type BuildFunction, type BuildPlanView } from "./buildPlanConstants";
import { CLASS_WALK_STAGES, DRINK_MIX_CLASS, SPARKLING_CLASS, type Stage } from "./constants";

export interface ReplenishmentTab {
  label: string;
  path: string;
  /** Which native cube the tab mounts. The pane switch picks on this, not the
   *  path, because the same cubes now mount under more than one base path
   *  (/replenishment, /supply, /sparkling, /drink-mix). Absent on report tabs. */
  kind?: "walk" | "production" | "transfers" | "allocation";
  /** The QueryViz report the tab embeds (scripts/seed_queryviz_reports.py owns
   *  the slug). Absent on the page's own cubes — Balance Walk, Production and
   *  Transfers. */
  reportSlug?: string;
  /** For a `production` tab, which build-site function it is pre-filtered to: the
   *  Sparkling page splits Production into In-Line (`fill`) and Repack (`kitting`).
   *  Absent = the whole plan (every other page's single Production tab). */
  facilityFunction?: BuildFunction;
  /** For a `walk` tab, the build-site function whose builds its First Pack stage
   *  edits and locks. Absent: the walk edits nothing. */
  editsFunction?: BuildFunction;
  /** For a `production` tab, the fields and period grains its pivot offers.
   *  Absent: every segment. */
  view?: BuildPlanView;
  /** For a `walk` tab, the stages it offers. Absent: every stage. */
  stages?: readonly Stage[];
  /** Off the bar, but still routed: its path, its report and every link to it
   *  keep working, and deleting this line puts the tab back. */
  hidden?: boolean;
  /** On the bar and live, but drawn quieter: a view kept for reference rather
   *  than planned on (the co-man's own inventory walk). */
  muted?: boolean;
}

/** The full Replenishment page's tabs. The native cubes carry a `kind` so the
 *  pane switch picks them without matching on a label or a path. Allocation
 *  sits beside Production because they are the two halves of one decision:
 *  what is built, and where it goes. */
const allTabs = (basePath: string): readonly ReplenishmentTab[] => [
  { label: "Balance Walk", path: basePath, kind: "walk" },
  { label: "Production", path: `${basePath}/production`, kind: "production" },
  // The transfer plan as an allocation question (LMN-495): 3PLs across, SKUs
  // down, weeks of supply before and after the plan lands. It REPLACES the
  // time-phased pivot — name AND address (Neal, 2026-09-28): an old /transfers
  // link opens the matrix, which is what the tab has always meant. The pivot
  // stays routed below as the dispatch read.
  { label: "Transfers", path: `${basePath}/transfers`, kind: "allocation" },
  { label: "Transfer Schedule", path: `${basePath}/transfer-schedule`, kind: "transfers", hidden: true },
  // The two reports the native cubes now answer for. Hidden rather than
  // removed: the page is what a planner reads, and the report is still the
  // thing it is crossed against.
  { label: "Build Plan", path: `${basePath}/build-plan`, reportSlug: "BuildPlanEngine", hidden: true },
  { label: "Transfer Plan", path: `${basePath}/transfer-plan`, reportSlug: "TransferPlan", hidden: true },
];
export const REPLENISHMENT_TABS: readonly ReplenishmentTab[] = allTabs("/replenishment");

/** The tab a path shows — every tab, hidden ones included, so an address typed
 *  or bookmarked for one still opens it. Anything no tab below the first
 *  claims is the first, so an unknown /<base>/<x> lands on the page rather
 *  than nowhere. Matched on the path rather than on reportSlug: the native
 *  tabs have no slug. */
export const tabAt = (
  tabs: readonly ReplenishmentTab[],
  pathname: string,
): ReplenishmentTab => {
  // The first tab is the fallback and is excluded from the match: its path is a
  // prefix of every other tab's, so including it would swallow them all.
  const [fallback, ...rest] = tabs;
  return (
    rest.find((t) => pathname === t.path || pathname.startsWith(`${t.path}/`)) ?? fallback
  );
};

/** Which tabs a base path mounts. "all" is the Replenishment page as it is
 *  (and, under another base, its twin — /replenishmentsplittest); "walk" is
 *  just the Balance Walk (the Supply page); "plan" is Production + Transfers
 *  (the per-class pages). Paths hang off the given base so the same cubes can
 *  live under /supply, /sparkling and /drink-mix without colliding with
 *  /replenishment's addresses. */
export type ReplenishmentTabMode = "all" | "walk" | "plan";

export const makeReplenishmentTabs = (
  basePath: string,
  mode: ReplenishmentTabMode,
  /** Plan on the walks: a read-only Balance Walk offering these stages replaces
   *  Production, and the plan is edited on the Build Plan tab. A walk with a First
   *  Pack stage has a repack site, whose own walk is a tab too. Absent: Production
   *  is one tab over the whole plan, and the page has no walk. */
  walkStages?: readonly Stage[],
  /** The page's Build Plan tab, for a class page that has one — each product
   *  line's own view (its upload group is what the tab's lock status and its
   *  save are for). Sparkling's by default on a page with a repack site. */
  buildPlanView: BuildPlanView | undefined = walkStages?.includes("first_pack") ? BUILD_PLAN_VIEW : undefined,
): readonly ReplenishmentTab[] => {
  const repackSite = Boolean(walkStages?.includes("first_pack"));
  if (mode === "walk") return [{ label: "Balance Walk", path: basePath, kind: "walk" }];
  if (mode === "plan")
    return [
      ...(walkStages
        ? ([{ label: "Balance Walk", path: basePath, kind: "walk", stages: walkStages }] as const)
        : ([{ label: "Production", path: basePath, kind: "production" }] as const)),
      ...(buildPlanView
        ? ([
            {
              label: "Build Plan",
              path: `${basePath}/build-plan`,
              kind: "production",
              view: buildPlanView,
            },
          ] as const)
        : []),
      { label: "Transfers", path: `${basePath}/transfers`, kind: "allocation" },
      // The repack site's own inventory walk — stock at the co-man in the filled
      // item — last and quiet: reference beside the planning views, not one of them.
      ...(repackSite
        ? ([
            {
              label: "Co-Man Inventory",
              path: `${basePath}/repack-site`,
              kind: "walk",
              stages: ["repack_site"],
              muted: true,
            },
          ] as const)
        : []),
      // Off the bar, still routed: the dispatch read (trucks, fill, weeks).
      { label: "Transfer Schedule", path: `${basePath}/transfer-schedule`, kind: "transfers", hidden: true },
    ];
  return basePath === "/replenishment" ? REPLENISHMENT_TABS : allTabs(basePath);
};

/** The tabs one mount of the page carries (ReplenishmentViewProps). A class
 *  page plans on its Balance Walk; any other plan page keeps one Production tab
 *  over the whole plan. */
export const pageTabs = (
  basePath: string,
  mode: ReplenishmentTabMode,
  lockedClass?: string,
): readonly ReplenishmentTab[] =>
  makeReplenishmentTabs(
    basePath,
    mode,
    lockedClass ? CLASS_WALK_STAGES[lockedClass] : undefined,
    // Each class page's Build Plan tab is its own product line's.
    lockedClass === SPARKLING_CLASS
      ? BUILD_PLAN_VIEW
      : lockedClass === DRINK_MIX_CLASS
        ? DRINK_MIX_BUILD_PLAN_VIEW
        : undefined,
  );
